import angular from 'angular';
import ngeoStatemanagerLocation from 'ngeo/statemanager/Location.js';
import * as olExtent from 'ol/extent.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';
import olLayerVector from 'ol/layer/Vector.js';
import olSourceVector from 'ol/source/Vector.js';
import olGeomPoint from 'ol/geom/Point.js';
import olOverlay from 'ol/Overlay.js';
import olStyleStyle from 'ol/style/Style.js';
import olStyleFill from 'ol/style/Fill.js';
import olStyleStroke from 'ol/style/Stroke.js';
import olStyleCircle from 'ol/style/Circle.js';

/**
 * Iframe search service, display result on map and iframe tooltip
 * @private
 */
class IframeSearchService {
  /**
   * @param {angular.IHttpService} $http Angular $http service.
   * @param {!string} iframeSearchUrl .
   * @param {angular.gettext.gettextCatalog} gettextCatalog Gettext catalog.
   * @param {import("ngeo/statemanager/Location.js").StatemanagerLocation} ngeoLocation ngeo location service.
   */
  constructor($http, iframeSearchUrl, gettextCatalog, ngeoLocation) {
    /**
     * @type {angular.IHttpService}
     * @private
     */
    this.$http_ = $http;

    /**
     * @type {!string}
     * @private
     */
    this.iframeSearchUrl_ = iframeSearchUrl;

    /**
     * @type {import("ngeo/statemanager/Location.js").StatemanagerLocation}
     * @private
     */
    this.ngeoLocation_ = ngeoLocation;

    /**
     * Preference for the '_' in private variable name here, could be done without...
     * @type {angular.gettext.gettextCatalog}
     * @private
     */
    this.gettextCatalog_ = gettextCatalog;
  }

  /**
   * Run a query for the iframe interface
   * @param {ol.Map} map .
   * @param {string} searchQuery .
   * @param {Object<string, ?>} dimensions .
   * @param {angular.Scope} $scope Scope.
   */
  runQuery(map, searchQuery, dimensions, $scope) {
    /**
     * @private
     */
    const extent = olExtent.createEmpty();

    /**
     * @private
     */
    const extentBuffer = 25;

    const radiusFeatureStyle = 18;

    const queryFeatureStyle = new olStyleStyle({
      fill: new olStyleFill({color: [90, 255, 0, 0.5]}),
      image: new olStyleCircle({
        fill: new olStyleFill({color: [90, 255, 0, 0.5]}),
        radius: radiusFeatureStyle,
        stroke: new olStyleStroke({color: [80, 220, 0, 1], width: 2}),
      }),
      stroke: new olStyleStroke({color: [80, 220, 0, 1], width: 2}),
    });

    // We do the request
    const request = this.$http_.get(`${this.iframeSearchUrl_}&query=${searchQuery}`);

    // Promise
    request.then((result) => {
      const data = result.data;

      const geoJsonFormat = new olFormatGeoJSON();
      const features = geoJsonFormat.readFeatures(data);
      console.assert(features);

      const resultLayer = new olLayerVector({
        style: queryFeatureStyle,
        source: new olSourceVector({features}),
      });
      map.addLayer(resultLayer);

      let targetFloor;
      for (const feature of features) {
        if (targetFloor !== undefined && feature.get('floor') !== targetFloor) {
          targetFloor = undefined;
          break;
        }
        targetFloor = feature.get('floor');
      }

      if (targetFloor !== undefined) {
        dimensions['floor'] = targetFloor.toString();
      }

      // Single result handling
      if (features.length === 1) {
        const centerCoordinates = olExtent.getCenter(features[0].getGeometry().getExtent());
        this.createTooltip_(map, centerCoordinates, features[0], dimensions, $scope);
      }

      // Recenter map on extent
      features.forEach((feature) => {
        olExtent.extend(extent, feature.getGeometry().getExtent());
      });
      olExtent.buffer(extent, extentBuffer);
      const size = map.getSize();
      console.assert(size);
      map.getView().fit(extent, {size});
      if (features.length === 1 && this.ngeoLocation_.getParam('map_zoom') !== undefined) {
        map.getView().setZoom(parseInt(this.ngeoLocation_.getParam('map_zoom'), 10));
      }

      features.forEach((feature) => {
        const geom = feature.getGeometry();
        feature.set('original-geometry', geom, true);
        if (geom.getType() !== 'Point' && geom.getType() !== 'LineString') {
          const geomExtent = geom.getExtent();
          const centerCoord = olExtent.getCenter(geomExtent);
          const pointGeom = new olGeomPoint(centerCoord);
          feature.set('point-geometry', pointGeom, true);
        }
      });
      const circleRadius = radiusFeatureStyle * map.getView().getResolution();
      this.changeGeom(features, circleRadius);

      // Listen on resolution change, geometry changes
      map.getView().on('change:resolution', (evt) => {
        const circleRadius = radiusFeatureStyle * map.getView().getResolution();
        this.changeGeom(features, circleRadius);
      });

      // Tooltip
      const hitTolerance = 10;
      map.on('singleclick', (event) => {
        map.getOverlays().clear();

        map.forEachFeatureAtPixel(
          event.pixel,
          (feature, layer) => {
            const centerCoordinates = olExtent.getCenter(feature.getGeometry().getExtent());
            this.createTooltip_(
              map,
              centerCoordinates,
              /** @type {ol.Feature} */ (feature),
              dimensions,
              $scope,
            );
            return false;
          },
          {
            layerFilter(layer) {
              return layer === resultLayer;
            },
            hitTolerance,
          },
        );
      });

      // Mouse cursor
      map.on('pointermove', (event) => {
        const hovered = map.hasFeatureAtPixel(event.pixel, {
          layerFilter(layer) {
            return layer === resultLayer;
          },
          hitTolerance,
        });

        const element = document.querySelector('div.ol-unselectable');
        if (hovered) {
          element.classList.add('cursor-pointer');
        } else {
          element.classList.remove('cursor-pointer');
        }
      });
    });
  }

  /**
   * Create a tooltip element and add it as overlay to the map
   * @param {ol.Map} map .
   * @param {ol.Coordinate} coordinates .
   * @param {ol.Feature} feature .
   * @param {Object<string, ?>} dimensions .
   * @param {angular.Scope} $scope Scope.
   * @return {!Element} element Tooltip DOM element.
   * @private
   */
  createTooltip_(map, coordinates, feature, dimensions, $scope) {
    const element = document.createElement('div');
    element.classList.add('iframe-tooltip-container');

    let labelTxtLine = `<p><b>${feature.get('label')}</b></p>`;
    let roomTxtLine = '';
    const gettextCatalog = this.gettextCatalog_;

    if (feature.get('room')) {
      const urlRoom = `https://plan.epfl.ch/?room=${feature.get('room')}`.toString();
      roomTxtLine = `<p><span class="attribute-key">${gettextCatalog.getString(
        'local',
      )}</span> <a id="room-link" href="${urlRoom}" target="_blank" rel="noopener">${feature.get(
        'room',
      )}</a></p>`;
    }

    if (feature.get('layer_name') === 'persons') {
      const urlPerson = `https://search.epfl.ch/psearch.action?request_locale=${
        dimensions['lang']
      }&q=${feature.get('label')}`;
      labelTxtLine = `<p><b><a id="person-link" href="${urlPerson}" target="_blank" rel="noopener">${feature.get(
        'label',
      )}</a></b></p>`;
    }

    element.innerHTML = `<div class="tooltip-content">
                                  ${labelTxtLine}
                                  ${roomTxtLine}
                                  <p><span class="attribute-key">${gettextCatalog.getString(
                                    'floor',
                                  )}</span> <a id="floor-link">${feature.get('floor')}</a></p>
                                </div>`;

    document.body.appendChild(element);

    // Event listener for floor changes
    document.getElementById('floor-link').addEventListener('click', (evt) => {
      dimensions['floor'] = feature.get('floor').toString();
      $scope.$apply();
    });

    const arrow = document.createElement('div');
    arrow.classList.add('arrow');
    element.appendChild(arrow);

    const autoPan = true;
    const popup = new olOverlay({
      element,
      autoPan,
    });
    map.addOverlay(popup);

    popup.setPositioning('bottom-center');
    popup.setPosition(coordinates);

    return element;
  }

  /**
   * Change the polygon geometry if area is smaller than the provided circle area
   * @param {Array<ol.Feature>} features .
   * @param {number} circleRadius .
   */
  changeGeom(features, circleRadius) {
    const circleArea = Math.PI * Math.pow(circleRadius, 2);
    features.forEach((feature) => {
      const pointGeometry = /** @type(ol.geom.Point) */ (feature.get('point-geometry'));

      if (!pointGeometry) {
        return;
      }

      const originalGeometry = /** @type(ol.geom.MultiPolygon) */ (feature.get('original-geometry'));
      const targetGeom = circleArea > originalGeometry.getArea() ? pointGeometry : originalGeometry;
      if (targetGeom !== feature.getGeometry()) {
        feature.setGeometry(targetGeom);
      }
    });
  }
}
IframeSearchService.$inject = ['$http', 'iframeSearchUrl', 'gettextCatalog', 'ngeoLocation'];

/**
 * @type hidden
 */
const gmfModule = angular.module('iframeSearch', [ngeoStatemanagerLocation.name]);

gmfModule.service('iframeSearchService', IframeSearchService);

export default gmfModule;
